import { useEffect, useRef, useCallback } from 'react';

import { useFormContext } from 'react-hook-form';
import { isUndefined } from 'lodash-es';

import { useYupValidation } from '@utils/form-validation/react-hook-form';

import { BodySchema } from './schema';
import type { Body } from '../../hooks/flight';

export * from '@utils/form-validation/react-hook-form';

export const validateChildOrInfantAge = (
  age?: number,
  birthDate?: string,
  message?: string,
  isAgeValid?: (age?: number, birthDate?: string) => boolean
) => {
  if (age === undefined) return '';
  return isAgeValid?.(age, birthDate) ? '' : message;
};

export const checkValidateChildAge = (age?: number) =>
  age ? age >= 2 && age < 12 : isUndefined(age) ? true : false;

export const checkValidateAdultAge = (age?: number) =>
  age ? age >= 12 : isUndefined(age) ? true : false;

export const checkValidateChildAgePackage = (age?: number) =>
  age !== undefined ? age >= 0 && age < 13 : isUndefined(age) ? true : false;

export const checkValidateInfantAge = (age?: number) =>
  age ? age >= 0 && age < 2 : true;
export const useReservationInformationValidation = (
  body: Body,
  fn?: (b: Body) => void
) => {
  const validationInformation = useYupValidation(body, {
    validationPaths: ['travelers'],
    fn,
    defaultValues: body,
    schema: BodySchema,
  });
  const {
    methods: {
      formState: { submitCount, isValid, errors },
    },
  } = validationInformation;
  const trackFormErrors = useScrollToFirstError(
    submitCount,
    isValid,
    '[data-error="true"]'
  );
  return { ...validationInformation, errors, trackFormErrors };
};

export default useReservationInformationValidation;

const useWrap = () => useFormContext<Body>();
type FormErrors = ReturnType<typeof useWrap>['formState']['errors'];
export const useReservationFormErrors = <Key extends keyof FormErrors>(
  key: Key
): {
  error: FormErrors[Key];
  isDirty: boolean;
  isSubmitted: boolean;
} => {
  const {
    formState: { errors, touchedFields, isSubmitted },
  } = useFormContext<Body>();
  return {
    error: errors[key],
    isDirty: touchedFields[key as keyof typeof touchedFields] !== undefined,
    isSubmitted,
  };
};

export const useTravelersErrors = () => useReservationFormErrors('travelers');

const useScrollToFirstError = (
  submitCount: number,
  isValid: boolean,
  errorSelector: string
) => {
  const rootRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isValid && submitCount > 0) {
      setTimeout(() => {
        rootRef.current?.querySelector(errorSelector)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }, 0);
    }
  }, [submitCount, isValid, errorSelector]);
  const trackFormErrors = useCallback((root: HTMLElement | null) => {
    if (rootRef) {
      rootRef.current = root;
    }
  }, []);
  return trackFormErrors;
};
